// packages
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { ArrowLeft, Save, Sparkles, Trash2, Loader2 } from 'lucide-react'

// components
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { PageHeader } from '@/components/common/page-header'
import { GeneralSection } from '@/components/signs-symptoms/general-section'
import { GestationalSection } from '@/components/signs-symptoms/gestational-section'
import { ResultDialog } from '@/components/signs-symptoms/result-dialog'
import { resolveSymptomMetadata, resolveGestationalSymptomMetadata } from '@/components/signs-symptoms/data'

// libs
import { showConfirm } from '@/lib/alerts'

// hooks
import { useToast } from '@/hooks/use-toast'

// store
import { useSignsSymptomsStore } from '@/store/signs-symptoms'

// entities
import {
  SignsSymptomFormSchema,
  signsSymptomFormSchema,
  validateSignsSymptomCrossFields,
  SymptomChecks,
  TrimesterChecks,
  emptyTrimesterChecks,
  guessActiveTrimester,
  signs_symptom_category,
  signs_symptom_children_range,
  signs_symptom_status,
  formatted_signs_symptom_category,
  formatted_signs_symptom_children_range
} from '@/entities/signs-symptom'

const loc = '@/pages/SignsSymptomsForm'

export default function SignsSymptomsForm() {
  const navigate = useNavigate()
  const { ref } = useParams<{ ref?: string }>()
  const { toast } = useToast()

  const items = useSignsSymptomsStore(s => s.items)
  const fetchByRef = useSignsSymptomsStore(s => s.fetchByRef)
  const createRec = useSignsSymptomsStore(s => s.create)
  const updateRec = useSignsSymptomsStore(s => s.update)
  const generateAnalysis = useSignsSymptomsStore(s => s.generateAnalysis)

  const [existing, setExisting] = useState(() => (ref ? items.find(i => i.ref === ref) : null) || null)
  const isNew = !existing

  const [general, setGeneral] = useState<SymptomChecks>(existing?.generalSymptoms ?? {})
  const [gestational, setGestational] = useState<TrimesterChecks>(existing?.gestationalSymptoms ?? emptyTrimesterChecks())
  const [showResult, setShowResult] = useState(false)
  const [savedRecordRef, setSavedRecordRef] = useState<string | null>(existing?.ref ?? null)
  const dirtyRef = useRef(false)

  const form = useForm<SignsSymptomFormSchema>({
    resolver: zodResolver(signsSymptomFormSchema),
    defaultValues: {
      patientName: existing?.patientName || '',
      category: existing?.category || signs_symptom_category.GESTANTE,
      weeksOfGestation: existing?.weeksOfGestation || undefined,
      childrenRange: existing?.childrenRange ?? null
    }
  })

  const formValues = useWatch({ control: form.control })
  const isGestante = formValues.category === signs_symptom_category.GESTANTE
  const isCrianca = formValues.category === signs_symptom_category.CHILDREN

  // Carrega registro remoto se não tem ainda
  useEffect(() => {
    if (!ref) return
    if (existing) return
    fetchByRef(ref).then(record => {
      if (record) {
        setExisting(record)
        setGeneral(record.generalSymptoms ?? {})
        setGestational(record.gestationalSymptoms ?? emptyTrimesterChecks())
        form.reset({
          patientName: record.patientName,
          category: record.category,
          weeksOfGestation: record.weeksOfGestation ?? undefined,
          childrenRange: record.childrenRange ?? null
        })
      }
    })
  }, [ref])

  // Detecta dirty
  useEffect(() => {
    const initial = JSON.stringify({
      patientName: existing?.patientName || '',
      category: existing?.category || signs_symptom_category.GESTANTE,
      weeksOfGestation: existing?.weeksOfGestation || null,
      childrenRange: existing?.childrenRange ?? null,
      general: existing?.generalSymptoms ?? {},
      gestational: existing?.gestationalSymptoms ?? emptyTrimesterChecks()
    })
    const current = JSON.stringify({
      patientName: formValues.patientName || '',
      category: formValues.category || signs_symptom_category.GESTANTE,
      weeksOfGestation: formValues.weeksOfGestation || null,
      childrenRange: formValues.childrenRange ?? null,
      general,
      gestational
    })
    dirtyRef.current = initial !== current
  }, [formValues, general, gestational, existing])

  // Quando categoria muda, limpa campos condicionais
  useEffect(() => {
    if (!isGestante) form.setValue('weeksOfGestation', undefined as never)
    if (!isCrianca) form.setValue('childrenRange', null as never)
    else if (!form.getValues('childrenRange')) form.setValue('childrenRange', signs_symptom_children_range.R_0_6M)
  }, [isGestante, isCrianca])

  // beforeunload
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (dirtyRef.current) {
        e.preventDefault()
        e.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [])

  useEffect(() => {
    const onClickCapture = (e: MouseEvent) => {
      if (!dirtyRef.current) return
      const target = e.target as HTMLElement | null
      if (!target) return
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null
      if (!anchor) return
      const href = anchor.getAttribute('href')
      if (!href || href.startsWith('http') || href.startsWith('#')) return
      if (anchor.closest('[data-signs-form="container"]')) return
      e.preventDefault()
      e.stopPropagation()
      ;(async () => {
        const { isConfirmed } = await showConfirm('Existem alterações não salvas. Deseja sair sem salvar?')
        if (isConfirmed) navigate(href)
      })()
    }
    document.addEventListener('click', onClickCapture, true)
    return () => document.removeEventListener('click', onClickCapture, true)
  }, [navigate])

  const navigateGuarded = async (to: string) => {
    if (!dirtyRef.current) {
      navigate(to)
      return
    }
    const { isConfirmed } = await showConfirm('Existem alterações não salvas. Deseja sair sem salvar?')
    if (isConfirmed) navigate(to)
  }

  const onCategoryChange = async (next: signs_symptom_category) => {
    const current = form.getValues('category')
    if (next === current) return
    const totalGest = Object.values(gestational).reduce((acc, t) => acc + Object.values(t).filter(Boolean).length, 0)
    const hasFilled =
      Object.values(general).some(Boolean) ||
      totalGest > 0 ||
      !!form.getValues('weeksOfGestation')
    if (hasFilled) {
      const { isConfirmed } = await showConfirm('A mudança de categoria irá limpar os sintomas marcados e dados relacionados. Deseja continuar?')
      if (!isConfirmed) return
      setGeneral({})
      setGestational(emptyTrimesterChecks())
      form.setValue('weeksOfGestation', undefined as never)
    }
    form.setValue('category', next)
  }

  const totalSymptoms = useMemo(() => {
    const gen = Object.values(general).filter(Boolean).length
    const gest = isGestante ? Object.values(gestational).reduce((acc, t) => acc + Object.values(t).filter(Boolean).length, 0) : 0
    return gen + gest
  }, [general, gestational, isGestante])

  const suggestedTrim = useMemo(() => guessActiveTrimester(formValues.weeksOfGestation as number | undefined), [formValues.weeksOfGestation])

  const persistRecord = async (): Promise<string | null> => {
    const values = form.getValues()
    const payload = {
      patientName: values.patientName,
      category: values.category,
      weeksOfGestation: isGestante ? values.weeksOfGestation ?? null : null,
      childrenRange: isCrianca ? values.childrenRange ?? null : null,
      generalSymptoms: general,
      generalSymptomsResolved: resolveSymptomMetadata(general),
      // Sintomas gestacionais (apenas para Gestante)
      gestationalSymptoms: isGestante ? gestational : null,
      gestationalSymptomsResolved: isGestante ? resolveGestationalSymptomMetadata(gestational) : null
    }
    if (savedRecordRef) {
      const updated = await updateRec(savedRecordRef, payload)
      if (!updated) return null
      setExisting(updated)
      dirtyRef.current = false
      return updated.ref
    }
    const created = await createRec(payload)
    if (!created) return null
    setExisting(created)
    setSavedRecordRef(created.ref)
    dirtyRef.current = false
    return created.ref
  }

  const [isSaving, setIsSaving] = useState(false)
  const [isGenerating, setIsGenerating] = useState(false)
  const isBusy = isSaving || isGenerating

  const onSave = form.handleSubmit(async values => {
    const fullLoc = `${loc}.onSave`
    if (isBusy) return
    try {
      const crossErr = validateSignsSymptomCrossFields(values)
      if (crossErr) {
        toast({ variant: 'destructive', title: 'Atenção', description: crossErr })
        return
      }
      const { isConfirmed } = await showConfirm(
        isNew ? `Salvar o registro de ${values.patientName}?` : `Salvar alterações em ${values.patientName}?`
      )
      if (!isConfirmed) return
      setIsSaving(true)
      const newRef = await persistRecord()
      if (!newRef) {
        toast({ variant: 'destructive', title: 'Ops', description: 'Não foi possível salvar.' })
        return
      }
      toast({ variant: 'success', title: 'Sucesso', description: `Registro de ${values.patientName} salvo.` })
      navigate('/signs-symptoms')
    } catch (err) {
      console.error(`Unhandled rejection at ${fullLoc}.`, err)
      toast({ variant: 'destructive', title: 'Ops', description: 'Não foi possível salvar.' })
    } finally {
      setIsSaving(false)
    }
  })

  const onGenerate = async () => {
    const fullLoc = `${loc}.onGenerate`
    if (isBusy) return
    const isValid = await form.trigger()
    if (!isValid) return
    const values = form.getValues()
    const crossErr = validateSignsSymptomCrossFields(values)
    if (crossErr) {
      toast({ variant: 'destructive', title: 'Atenção', description: crossErr })
      return
    }
    if (totalSymptoms === 0) {
      toast({ variant: 'destructive', title: 'Atenção', description: 'Marque ao menos um sintoma para gerar a análise.' })
      return
    }
    const { isConfirmed } = await showConfirm('Gerar análise com a Nina? O registro será salvo automaticamente e a análise será processada em segundo plano.')
    if (!isConfirmed) return
    try {
      setIsGenerating(true)
      // Garante que está salvo antes de pedir análise
      const newRef = await persistRecord()
      if (!newRef) {
        toast({ variant: 'destructive', title: 'Ops', description: 'Não foi possível salvar antes de gerar a análise.' })
        return
      }
      const result = await generateAnalysis(newRef)
      if (result && 'error' in result) {
        toast({ variant: 'destructive', title: 'Ops', description: result.error })
        return
      }
      toast({ variant: 'success', title: 'Análise enfileirada', description: 'Você será notificado quando a Nina concluir a análise.' })
      navigate('/signs-symptoms')
    } catch (err) {
      console.error(`Unhandled rejection at ${fullLoc}.`, err)
      toast({ variant: 'destructive', title: 'Ops', description: 'Não foi possível gerar a análise.' })
    } finally {
      setIsGenerating(false)
    }
  }

  const onClearAll = async () => {
    try {
      const { isConfirmed } = await showConfirm('Deseja limpar todas as marcações? Os dados do paciente serão preservados.')
      if (!isConfirmed) return
      setGeneral({})
      setGestational(emptyTrimesterChecks())
    } catch (err) {
      console.error(`Unhandled rejection at ${loc}.onClearAll.`, err)
    }
  }

  const onDownloadPdf = () => {
    toast({ variant: 'default', title: 'Em construção', description: 'O download em PDF estará disponível em breve.' })
  }

  const previewRecord = existing && existing.status === signs_symptom_status.ACTIVE ? existing : null

  return (
    <>
      <PageHeader
        title={isNew ? 'Novo registro de Sinais e Sintomas' : `Editar — ${existing?.patientName}`}
        description="Marque os sintomas presentes para gerar uma análise clínica com apoio da Nina"
      />

      <Form {...form}>
        <form onSubmit={onSave} className="mt-4 space-y-5" data-signs-form="container">
          {/* Identificação */}
          <div className="rounded-2xl border border-border bg-card shadow-soft p-5 mdAndUp:p-6">
            <div className="flex items-center gap-2 mb-4">
              <h2 className="font-display text-lg font-semibold text-foreground">Identificação da paciente</h2>
            </div>
            <div className={`grid grid-cols-1 gap-4 mdAndUp:grid-cols-2 ${isGestante || isCrianca ? 'lgAndUp:grid-cols-3' : ''}`}>
              <FormField
                control={form.control}
                name="patientName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-foreground font-medium">Nome da paciente *</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Digite o nome completo" autoComplete="off" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-foreground font-medium">Categoria *</FormLabel>
                    <FormControl>
                      <Select value={field.value} onValueChange={v => onCategoryChange(v as signs_symptom_category)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione" />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.values(signs_symptom_category).map(c => (
                            <SelectItem key={c} value={c}>
                              {formatted_signs_symptom_category[c]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {isGestante && (
                <FormField
                  control={form.control}
                  name="weeksOfGestation"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-foreground font-medium">Semanas de gestação *</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="number"
                          min={1}
                          max={42}
                          inputMode="numeric"
                          placeholder="Ex.: 24"
                          value={typeof field.value === 'number' ? field.value : (field.value ?? '')}
                          onChange={e => field.onChange(e.target.value === '' ? undefined : Number(e.target.value))}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {isCrianca && (
                <FormField
                  control={form.control}
                  name="childrenRange"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-foreground font-medium">Faixa etária *</FormLabel>
                      <FormControl>
                        <Select value={(field.value as string | undefined) ?? ''} onValueChange={v => field.onChange(v as signs_symptom_children_range)}>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione" />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.values(signs_symptom_children_range).map(c => (
                              <SelectItem key={c} value={c}>
                                {formatted_signs_symptom_children_range[c]}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </div>
          </div>

          {isGestante && <GestationalSection value={gestational} onChange={setGestational} initialTrim={suggestedTrim} />}

          <GeneralSection value={general} onChange={setGeneral} />

          {/* Footer fixo de ações */}
          <div className="sticky bottom-0 -mx-3 mdAndUp:-mx-6 lgAndUp:-mx-12 px-3 mdAndUp:px-6 lgAndUp:px-12 py-3 mt-6 backdrop-blur-md bg-card/80 border-t border-border z-20">
            <div className="flex items-center justify-between gap-3 smAndDown:flex-col">
              <div className="flex items-center gap-2 text-sm text-muted-foreground smAndDown:w-full smAndDown:justify-center">
                <span>
                  <strong className="text-coentro font-semibold">{totalSymptoms}</strong> sintoma{totalSymptoms !== 1 ? 's' : ''} marcado{totalSymptoms !== 1 ? 's' : ''}
                </span>
              </div>
              <div className="flex items-center gap-2 smAndDown:w-full smAndDown:flex-col">
                <Button type="button" variant="outline" size="sm" onClick={() => navigateGuarded('/signs-symptoms')} className="gap-2 smAndDown:w-full" disabled={isBusy}>
                  <ArrowLeft className="h-4 w-4" />
                  Voltar
                </Button>
                {totalSymptoms > 0 && (
                  <Button type="button" variant="destructive" size="sm" onClick={onClearAll} className="gap-2 smAndDown:w-full" disabled={isBusy}>
                    <Trash2 className="h-4 w-4" />
                    Limpar
                  </Button>
                )}
                <Button type="submit" variant="default" size="sm" className="gap-2 font-semibold smAndDown:w-full" disabled={isBusy}>
                  {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  {isSaving ? 'Salvando...' : 'Salvar'}
                </Button>
                <Button type="button" size="sm" onClick={onGenerate} className="gap-2 font-semibold smAndDown:w-full bg-coentro text-white hover:bg-coentro/90" disabled={totalSymptoms === 0 || isBusy}>
                  {isGenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                  {isGenerating ? 'Gerando...' : 'Gerar análise'}
                </Button>
              </div>
            </div>
          </div>
        </form>
      </Form>

      <ResultDialog open={showResult} record={previewRecord} onClose={() => setShowResult(false)} onDownloadPdf={onDownloadPdf} />
    </>
  )
}
